import React, { useState, useEffect, useRef } from "react";
import {
  RgbStringColorPicker,
  HslaStringColorPicker,
  HslStringColorPicker,
  RgbaStringColorPicker,
} from "react-colorful";
import { colord, getFormat as getColorFormat } from "colord";
import { Icon } from "./Icon";
import { useDebounce } from "../_hooks";

const defaultSwatches = [
  "rgb(209, 68, 191)", //Dark Brown
  "rgb(161, 46, 46)", // Dark Red (Brand)
  "rgb(14, 90, 9)", //Dark Green
  "rgb(19, 114, 101)", //Dark Teal
  "rgb(25, 19, 114)", // Dark Purple
  "rgb(114, 19, 102)", // Dark Magenta
  "rgb(155, 140, 105)", // Light Brown
  "rgb(205, 3, 3)", // Light Red
  "rgb(33, 124, 27)", // Light Green
  "rgb(14, 193, 168)", // Light Teal
  "rgb(64, 57, 169)", // Light Purple
  "rgb(209, 68, 191)", // Light Magenta
  //-----------------------
  // "rgb(200,0,0)", //red
  // "rgb(0,0,200)", //blue
  // "rgb(0,200,0)", //green
  // "rgb(255,128,0)", //orange
  // "rgb(200,0,200)", //purple
  // "rgb(0,150,150)", //cyan
  // "rgb(0,0,0)", //black
  // "rgb(128,128,128)", //grey
  // "rgb(255,255,255)", //white
  // 'rgb(157,105,77)', //lightbrown
  // 'rgb(210,48,45)', //pastelred
  // 'rgb(196,30,100)', //fuschia
  // 'rgb(105,36,140)', //purple
  // 'rgb(18,104,175)', //blue
  // 'rgb(36,138,128)', //sagegreen
  // 'rgb(45,157,73)', //forestgreen
  // 'rgb(225,123,41)', //burntcienna
  // 'rgb(207,91,60)', //pastelorange
  // 'rgb(157,105,77)', //brown
  // 'rgb(94,106,123)', //periwinkle
  // 'rgb(102,116,119)', //grayblue
  // 'rgb(97,95,98)', //gunmetal
];

export function ColorPicker({
  value,
  onChange,
  startOpen,
  keepOpen,
  disableSwatches,
  maxSwatches,
  colorMode = "rgba",
  clearable = true,
}) {
  const [expanded, setExpanded] = useState(startOpen);
  const [recentColors, setRecentColors] = useState([]);
  const [color, setColor] = useState(value);
  const storageKey = "chronos-recent-colors";
  const MAX_RECENT_COLORS = 5;
  const pickerRef = useRef();
  const closePicker = () => {
    setExpanded(false);
  };
  const handleToggle = () => {
    handleRecents(color);
    setExpanded(!expanded);
  };

  useEffect(() => {
    const handleClickOutside = (ev) => {
      if (!keepOpen) {
        if (pickerRef.current && !pickerRef.current.contains(ev.target)) {
          closePicker();
        }
      }
    };
    if (expanded) {
      window.addEventListener("click", handleClickOutside);
    }
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  });

  const handleChange = (value) => {
    console.log("Event", value);
    // if (onChange) {
    //   onChange(value);
    // }
    // if (colord(value).isValid()) {
    //   handleRecents(value);
    // }
    setColor(value);
  };

  useDebounce(
    () => {
      if (onChange) {
        console.log("Should update filter.");
        onChange(color);
      }
    },
    300,
    [color]
  );

  const handleClear = () => {
    handleChange("");
    // if (onChange) {
    //   onChange('');
    // }
  };

  const handleSwatchSelect = (value) => {
    handleRecents(value);
    handleChange(value);
  };

  const handleRecents = (value) => {
    if (!disableSwatches) {
      if (!value || value.length === 0 || value === "") {
        return;
      }
      if (recentColors) {
        // const swatches = [...recentColors, ...defaultSwatches];
        const oldItemIndex = recentColors.findIndex((s) => s === value);

        if (oldItemIndex > -1) {
          let newRecents = [
            value,
            ...recentColors.slice(0, oldItemIndex),
            ...recentColors.slice(oldItemIndex + 1),
          ];
          storeRecents(newRecents);
        } else {
          let newRecents = [value, ...recentColors];
          storeRecents(newRecents);
        }
        // const newColors = [value, ...recentColors]
      }
    }
  };

  const storeRecents = (recents) => {
    if (recents) {
      if (recents.length > MAX_RECENT_COLORS) {
        recents = recents.slice(0, MAX_RECENT_COLORS - 1);
      }
      localStorage.setItem(storageKey, JSON.stringify(recents));
      setRecentColors(recents);
    }
  };

  const convertColorFormat = (colorString) => {
    //if the color string is the wrong format, we need to correct it
    if (!colord(colorString).isValid()) {
      //We can't identify the format, so we cannot convert it.
      return colorString;
    }

    //Get the color format
    if (getColorFormat(colorString) !== colorMode) {
      if (colorMode === "rgb" || colorMode === "rgba") {
        return colord(colorString).toRgbString();
      }
      if (colorMode === "hsl") {
        return colord(colorString).toHslString();
      }
      // if (colorMode === 'hsla') {
      //   const colorReference = colord(colorString);
      //   let newColorString = colord('#000000');
      //   newColorString.hue(colorReference.hue());
      //   newColorString.saturate(colorReference.toHsl().s);
      //   newColorString.lighten(colorReference.toHsl().l);
      //   newColorString.alpha(colorReference.alpha());
      //   console.log(newColorString, newColorString.toHslString(), colorString);
      //   // return colorString;
      //   return newColorString.toHslString();
      // }
    }

    //Color is correct format already, return it
    return colorString;
  };

  useEffect(() => {
    if (!disableSwatches) {
      const recentColors =
        localStorage.getItem(storageKey) !== undefined &&
        localStorage.getItem(storageKey) !== null
          ? JSON.parse(localStorage.getItem(storageKey))
          : [];
      if (recentColors.length >= MAX_RECENT_COLORS) {
        setRecentColors(recentColors.slice(0, MAX_RECENT_COLORS - 1));
      } else {
        setRecentColors(recentColors);
      }
    }

    // console.log('COLOR VALID:', colord(value).isValid(), value);
    // console.log('COLOR VAL:', value);
    // console.log('FORMAT:', getColorFormat(value));
    // console.log('ASRGB', colord(value).toRgbString());
    // console.log('ASHSL', colord(value).toHslString());
  }, []);

  let swatches = [
    ...recentColors,
    ...defaultSwatches.filter((c) => recentColors.indexOf(c) === -1),
  ];
  if (maxSwatches && Number.isInteger(maxSwatches)) {
    swatches = swatches.slice(0, maxSwatches - 1);
  }
  return (
    <div className="chronos-color-picker" ref={pickerRef}>
      <div className="chronos-color-input-select">
        <span
          className="chronos-color-input-value"
          style={{
            backgroundColor: colord(color).isValid()
              ? convertColorFormat(color)
              : "default",
          }}
        ></span>
        <input
          type="text"
          value={convertColorFormat(color)}
          onChange={(e) => handleChange(e.target.value)}
          // disabled
        />
        {clearable && color !== "" && (
          <button
            className="chronos-input-icon chronos-clear-input"
            type="button"
            onClick={handleClear}
          >
            <Icon icon="far fa-times fa-fw" />
          </button>
        )}
        <button
          className="chronos-color-picker-toggle"
          type="button"
          onClick={handleToggle}
        >
          <Icon icon="far fa-chevron-down" />
        </button>
      </div>
      {expanded && (
        <div className="chronos-color-input-dropdown">
          {colorMode === "rgb" && (
            <RgbStringColorPicker color={color} onChange={handleChange} />
          )}
          {colorMode === "rgba" && (
            <RgbaStringColorPicker color={color} onChange={handleChange} />
          )}
          {colorMode === "hsl" && (
            <HslStringColorPicker color={color} onChange={handleChange} />
          )}
          {colorMode === "hsla" && (
            <HslaStringColorPicker
              // color={() => convertColorFormat(value)}
              color={color}
              onChange={handleChange}
            />
          )}

          {!disableSwatches && (
            <div className="chronos-color-input-swatch-container">
              <div className="chronos-color-input-swatches">
                {swatches &&
                  swatches.map((color, i) => {
                    return (
                      <Swatch
                        color={color}
                        key={i}
                        select={handleSwatchSelect}
                      />
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Swatch({ color, select }) {
  const swatchStyle = {
    backgroundColor: color,
  };
  const handleClick = (ev) => {
    if (select) {
      select(color.toLowerCase());
    }
  };
  return (
    <div
      className="chronos-color-input-swatch"
      style={swatchStyle}
      onClick={handleClick}
    ></div>
  );
}
